import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay, throttleTime } from 'rxjs/operators';
import { SnackBarService } from '../shared/snackbar.service';
import { ELDGroupResponse } from '../types';

@Injectable({
  providedIn: 'root',
})
export class EldService {
  private eldSystems$$ = new BehaviorSubject<{ id: number; name: string }[]>(null);
  private eldSystemsThrottle$$ = new Subject();
  private eldSystemsShared$ = this.eldSystems$$.asObservable().pipe(shareReplay(1));
  public get eldSystems$(): Observable<{ id: number; name: string }[]> {
    this.eldSystemsThrottle$$.next();
    return this.eldSystemsShared$;
  }

  private groupDrivers$$ = new BehaviorSubject<ELDGroupResponse>(null);
  private groupDriversThrottle$$ = new Subject();
  private groupDriversShared$ = this.groupDrivers$$.asObservable().pipe(shareReplay(1));
  public get groupDrivers$(): Observable<ELDGroupResponse> {
    this.groupDriversThrottle$$.next();
    return this.groupDriversShared$;
  }

  constructor(private httpClient: HttpClient, private logger: NGXLogger, private snackbar: SnackBarService) {
    this.groupDriversThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadEldGroupDrivers();
    });

    this.eldSystemsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadEldSystems();
    });
  }

  private async loadEldGroupDrivers() {
    try {
      const result = await this.httpClient.get<ELDGroupResponse>(`${environment.api}/v1/admin/eld`).toPromise();
      this.groupDrivers$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadEldSystems() {
    try {
      const result = await this.httpClient
        .get<{ systems: { id: number; name: string }[] }>(`${environment.api}/v1/admin/eld/systems`)
        .toPromise();
      this.eldSystems$$.next(result.systems);
    } catch (error) {
      this.logger.error(error);
    }
  }

  public async moveDriverToGroup(groupId: number, driverId: string) {
    try {
      const result = await this.httpClient
        .post<ELDGroupResponse>(`${environment.api}/v1/admin/eld/swap`, {
          groupId,
          driverId,
        })
        .toPromise();
      this.groupDrivers$$.next(result);
    } catch (error) {
      this.logger.error(error);
      this.snackbar.showError('failed to move driver, refreshing the driver list');
      this.loadEldGroupDrivers();
    }
  }

  public async addNewGroup(req: { eldId: number; name: string; apiKey: string }): Promise<void> {
    try {
      const result = await this.httpClient.post<ELDGroupResponse>(`${environment.api}/v1/admin/eld`, req).toPromise();
      this.groupDrivers$$.next(result);
    } catch (error) {
      this.logger.error(error);
      this.snackbar.showError('failed to create new group, refreshing the driver list');
      this.loadEldGroupDrivers();
    }
  }

  public async updateName(groupId: number, name: string): Promise<boolean> {
    try {
      const result = await this.httpClient
        .put<ELDGroupResponse>(`${environment.api}/v1/admin/eld/${groupId}/name`, { name })
        .toPromise();
      this.groupDrivers$$.next(result);
      this.snackbar.showMessage('Name has been updated');
      return true;
    } catch (error) {
      this.logger.error(error);
      this.snackbar.showError('failed to rename group, refreshing the list');
      this.loadEldGroupDrivers();
      return false;
    }
  }

  public async updateAPIKey(groupId: number, apiKey: string): Promise<boolean> {
    try {
      const result = await this.httpClient
        .put<ELDGroupResponse>(`${environment.api}/v1/admin/eld/${groupId}/api_key`, { apiKey })
        .toPromise();
      this.groupDrivers$$.next(result);
      this.snackbar.showMessage('API Key has been updated');
      return true;
    } catch (error) {
      this.logger.error(error);
      this.snackbar.showError('failed to update API key, refreshing the list');
      this.loadEldGroupDrivers();
      return false;
    }
  }

  public async deleteGroup(groupId: number): Promise<boolean> {
    try {
      const result = await this.httpClient
        .delete<ELDGroupResponse>(`${environment.api}/v1/admin/eld/${groupId}`)
        .toPromise();
      this.groupDrivers$$.next(result);
      this.snackbar.showMessage('Group has been deleted');
      return true;
    } catch (error) {
      this.logger.error(error);
      this.snackbar.showError('failed to delete, refreshing the list');
      this.loadEldGroupDrivers();
      return false;
    }
  }
}
